import { API } from '../config';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import {
  ChevronLeft, Send, Edit3, X, Check, Search, Phone, Users, User, Hash,
  RefreshCw, CheckCircle, XCircle, Zap, ClipboardPaste, UserCheck, UserX,
  Pause, Play, ImagePlus, Trash2
} from 'lucide-react';
import { toast } from 'sonner';

const STEPS = { MODE: 0, TEMPLATE: 1, PASTE: 2, CHECKING: 3, RECIPIENTS: 4, SENDING: 5, SUMMARY: 6 };
const MAX_NUMBERS = 2000;
const MAX_TEMPLATE = 5000;
const SEND_DELAY_MIN = 20000; // 20 saniye
const SEND_DELAY_MAX = 40000; // 40 saniye

export default function QuickSendPage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEPS.MODE);
  const [mode, setMode] = useState(null);
  const [template, setTemplate] = useState('Kanka yeni numaram bu: +90XXXXXXXXXX Kaydeder misin?');
  const [uploadedImage, setUploadedImage] = useState(null); // {filename, size}
  const [uploading, setUploading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [sendingAccount, setSendingAccount] = useState(null);
  const [pastedNumbers, setPastedNumbers] = useState('');
  const [checking, setChecking] = useState(false);
  const [registered, setRegistered] = useState([]);
  const [notRegistered, setNotRegistered] = useState([]);
  const [dialogs, setDialogs] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [searchQ, setSearchQ] = useState('');
  const [loadingDialogs, setLoadingDialogs] = useState(false);

  // Sending
  const [recipients, setRecipients] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [stats, setStats] = useState({ sent: 0, skipped: 0, failed: 0 });
  const [results, setResults] = useState([]);
  const [countdown, setCountdown] = useState(0);
  const pausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const sendingRef = useRef(false);
  const countdownRef = useRef(null);

  const ini = (n) => n ? n.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  const ac = (n) => { const c = ['bg-blue-600','bg-emerald-600','bg-amber-600','bg-rose-600','bg-violet-600','bg-cyan-600','bg-orange-600','bg-teal-600']; let h = 0; for (let i = 0; i < (n||'').length; i++) h = n.charCodeAt(i) + ((h << 5) - h); return c[Math.abs(h) % c.length]; };

  useEffect(() => {
    axios.get(`${API}/telegram-user/accounts`, { headers: getAuthHeaders() })
      .then(res => { const a = (res.data.accounts || []).filter(x => x.status === 'active'); setAccounts(a); if (a.length === 1) setSendingAccount(a[0]); })
      .catch(() => {});
  }, [getAuthHeaders]);

  const loadDialogs = useCallback(async () => {
    if (!sendingAccount) return;
    setLoadingDialogs(true);
    try { const res = await axios.get(`${API}/telegram-user/dialogs?limit=200&session_id=${sendingAccount.id}`, { headers: getAuthHeaders() }); setDialogs(res.data.dialogs || []); }
    catch { toast.error('Sohbetler yüklenemedi'); }
    finally { setLoadingDialogs(false); }
  }, [getAuthHeaders, sendingAccount]);

  useEffect(() => { if (step === STEPS.RECIPIENTS && mode === 'contacts' && sendingAccount) loadDialogs(); }, [step, mode, sendingAccount, loadDialogs]);

  const checkNumbers = async () => {
    const lines = pastedNumbers.split(/[\n,;]+/).map(l => l.trim()).filter(l => l.length >= 7);
    if (!lines.length) { toast.error('Geçerli numara bulunamadı'); return; }
    if (lines.length > MAX_NUMBERS) { toast.error(`En fazla ${MAX_NUMBERS} numara kontrol edilebilir`); return; }
    setChecking(true); setStep(STEPS.CHECKING);
    try {
      const res = await axios.post(`${API}/telegram-user/check-numbers`, { phones: lines, session_id: sendingAccount?.id }, { headers: getAuthHeaders() });
      setRegistered(res.data.registered || []); setNotRegistered(res.data.not_registered || []);
      setSelected(new Set((res.data.registered || []).map(r => r.user_id)));
      setStep(STEPS.RECIPIENTS);
      toast.success(`${(res.data.registered || []).length} kayıtlı, ${(res.data.not_registered || []).length} kayıtsız`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Kontrol başarısız'); setStep(STEPS.PASTE); }
    finally { setChecking(false); }
  };

  const toggleSelect = (id) => { setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; }); };
  const filteredDialogs = dialogs.filter(d => !searchQ || d.name?.toLowerCase().includes(searchQ.toLowerCase()));

  // ===== AUTO-SEND =====
  const startSending = () => {
    let recs;
    if (mode === 'numbers') {
      recs = registered.filter(r => selected.has(r.user_id)).map(r => ({
        id: r.user_id, name: r.name, phone: r.phone, chat_type: 'private', session_id: sendingAccount?.id
      }));
    } else {
      recs = dialogs.filter(d => selected.has(d.id)).map(d => ({
        id: d.id, name: d.name, chat_type: d.chat_type, session_id: d.session_id || sendingAccount?.id
      }));
    }
    setRecipients(recs); setCurrentIdx(0);
    setStats({ sent: 0, skipped: 0, failed: 0 }); setResults([]);
    pausedRef.current = false; cancelledRef.current = false; setIsPaused(false);
    setStep(STEPS.SENDING);
    // Start auto-send loop
    setTimeout(() => autoSendLoop(recs, 0, template), 500);
  };

  const autoSendLoop = async (recs, idx, msg) => {
    sendingRef.current = true;
    for (let i = idx; i < recs.length; i++) {
      if (cancelledRef.current) break;
      while (pausedRef.current && !cancelledRef.current) {
        await new Promise(r => setTimeout(r, 300));
      }
      if (cancelledRef.current) break;

      setCurrentIdx(i);
      setIsSending(true);
      const r = recs[i];

      try {
        await axios.post(`${API}/telegram-user/send`, { chat_id: r.id, text: msg, session_id: r.session_id }, { headers: getAuthHeaders() });
        setResults(prev => [...prev, { ...r, status: 'sent' }]);
        setStats(prev => ({ ...prev, sent: prev.sent + 1 }));
      } catch (err) {
        setResults(prev => [...prev, { ...r, status: 'failed', error: err.response?.data?.detail || 'Hata' }]);
        setStats(prev => ({ ...prev, failed: prev.failed + 1 }));
      }

      setIsSending(false);

      // Random delay 20-40 seconds between sends
      if (i < recs.length - 1 && !cancelledRef.current) {
        const delay = Math.floor(Math.random() * (SEND_DELAY_MAX - SEND_DELAY_MIN + 1)) + SEND_DELAY_MIN;
        const delaySeconds = Math.ceil(delay / 1000);
        setCountdown(delaySeconds);

        // Countdown timer
        let remaining = delaySeconds;
        await new Promise(resolve => {
          countdownRef.current = setInterval(() => {
            if (cancelledRef.current) { clearInterval(countdownRef.current); resolve(); return; }
            if (pausedRef.current) return; // Don't decrement while paused
            remaining--;
            setCountdown(remaining);
            if (remaining <= 0) { clearInterval(countdownRef.current); resolve(); }
          }, 1000);
        });
        setCountdown(0);
      }
    }
    sendingRef.current = false;
    if (countdownRef.current) clearInterval(countdownRef.current);
    setStep(STEPS.SUMMARY);
  };

  const togglePause = () => { pausedRef.current = !pausedRef.current; setIsPaused(!isPaused); };

  const cancelSending = () => {
    cancelledRef.current = true;
    // Mark remaining as skipped
    const remaining = recipients.slice(currentIdx + (isSending ? 1 : 0));
    setResults(prev => [...prev, ...remaining.map(r => ({ ...r, status: 'skipped' }))]);
    setStats(prev => ({ ...prev, skipped: prev.skipped + remaining.length }));
    setStep(STEPS.SUMMARY);
  };

  const retryGroup = (status) => {
    const group = results.filter(r => r.status === status);
    if (!group.length) return;
    const newRecs = group.map(r => ({ ...r }));
    setRecipients(newRecs); setCurrentIdx(0);
    setStats(prev => ({ ...prev, [status === 'failed' ? 'failed' : 'skipped']: 0 }));
    setResults(prev => prev.filter(r => r.status !== status));
    pausedRef.current = false; cancelledRef.current = false; setIsPaused(false);
    setStep(STEPS.SENDING);
    setTimeout(() => autoSendLoop(newRecs, 0, template), 500);
  };

  const cur = recipients[currentIdx];
  const prog = recipients.length > 0 ? ((results.length) / recipients.length) * 100 : 0;
  const numCount = pastedNumbers.split(/[\n,;]+/).filter(l => l.trim().length >= 7).length;

  return (
    <div className="min-h-dvh bg-background flex flex-col">
      <div className="flex-shrink-0 bg-card/95 backdrop-blur border-b border-border/70 px-4 py-3 flex items-center gap-3 z-10">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => {
          if (step === STEPS.SENDING) { if (window.confirm('İptal etmek istediğinize emin misiniz?')) cancelSending(); }
          else if (step === STEPS.SUMMARY) navigate('/app');
          else if (step > STEPS.MODE) setStep(step - 1);
          else navigate('/app');
        }}><ChevronLeft className="w-5 h-5" /></Button>
        <Zap className="w-5 h-5 text-primary" />
        <h1 className="text-lg font-semibold font-display">Numaramı Paylaş</h1>
        <Badge variant="secondary" className="text-[10px] ml-auto">Hızlı Gönderim</Badge>
      </div>

      <div className="flex-1 overflow-y-auto" style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-lg mx-auto p-4 space-y-4">

          {/* MODE */}
          {step === STEPS.MODE && (
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-foreground text-center">Nasıl göndermek istersiniz?</h2>
              <button onClick={() => { setMode('numbers'); setStep(STEPS.TEMPLATE); }}
                className="w-full bg-card rounded-xl border border-border/70 p-5 text-left hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center"><ClipboardPaste className="w-5 h-5 text-primary" /></div>
                  <div><p className="text-sm font-semibold text-foreground">Numara Yapıştır</p><p className="text-xs text-muted-foreground">Numaraları yapıştırın, Telegram'a kayıtlı olanları bulalım</p></div>
                </div>
              </button>
              <button onClick={() => { setMode('contacts'); setStep(STEPS.TEMPLATE); }}
                className="w-full bg-card rounded-xl border border-border/70 p-5 text-left hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center"><Users className="w-5 h-5 text-primary" /></div>
                  <div><p className="text-sm font-semibold text-foreground">Mevcut Sohbetler</p><p className="text-xs text-muted-foreground">Telegram sohbet listesinden seçin</p></div>
                </div>
              </button>
              {accounts.length === 0 && (
                <div className="bg-destructive/10 rounded-xl p-4 text-center">
                  <p className="text-sm text-destructive">Bağlı Telegram hesabı bulunamadı.</p>
                  <Button variant="outline" size="sm" className="mt-2 rounded-xl text-xs" onClick={() => navigate('/app/telegram-user-login')}>Hesap Bağla</Button>
                </div>
              )}
            </div>
          )}

          {/* TEMPLATE + IMAGE + ACCOUNT */}
          {step === STEPS.TEMPLATE && (
            <div className="space-y-4">
              {/* Message Template - LARGE */}
              <div className="bg-card rounded-2xl border border-border/70 p-6 space-y-4">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Edit3 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Mesaj Şablonu</h2>
                    <p className="text-xs text-muted-foreground">Göndermek istediğiniz mesajı düzenleyin</p>
                  </div>
                </div>

                <textarea value={template} onChange={e => { if (e.target.value.length <= MAX_TEMPLATE) setTemplate(e.target.value); }} rows={8}
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none leading-relaxed"
                  placeholder="Mesajınızı buraya yazın..." />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">{template.length} / {MAX_TEMPLATE} karakter</p>
                </div>
              </div>

              {/* Image Upload */}
              <div className="bg-card rounded-2xl border border-border/70 p-6 space-y-4">
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center">
                    <ImagePlus className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-foreground">Görsel Ekle</h2>
                    <p className="text-xs text-muted-foreground">Mesajla birlikte gönderilecek görsel (isteğe bağlı)</p>
                  </div>
                </div>

                {uploadedImage ? (
                  <div className="flex items-center gap-3 bg-secondary/30 rounded-xl p-4">
                    <div className="w-16 h-16 rounded-xl bg-secondary flex items-center justify-center overflow-hidden flex-shrink-0">
                      {uploadedImage.preview ? (
                        <img src={uploadedImage.preview} alt="Önizleme" className="w-full h-full object-cover" />
                      ) : (
                        <ImagePlus className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{uploadedImage.filename}</p>
                      <p className="text-xs text-muted-foreground">{uploadedImage.size > 1048576 ? `${(uploadedImage.size / 1048576).toFixed(1)} MB` : `${Math.round(uploadedImage.size / 1024)} KB`}</p>
                      <Badge className="text-[9px] h-4 bg-green-500 text-white mt-1">Yüklendi</Badge>
                    </div>
                    <Button variant="ghost" size="icon" className="rounded-xl h-9 w-9 text-destructive flex-shrink-0" onClick={async () => {
                      try { await axios.delete(`${API}/telegram-user/upload-image`, { headers: getAuthHeaders() }); } catch {}
                      setUploadedImage(null);
                      toast.success('Görsel kaldırıldı');
                    }}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-3 border-2 border-dashed border-border/70 rounded-xl p-8 cursor-pointer hover:border-primary/50 hover:bg-secondary/20 transition-colors">
                    <div className="w-14 h-14 rounded-2xl bg-secondary/50 flex items-center justify-center">
                      <ImagePlus className="w-7 h-7 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-foreground">Görsel Seçin</p>
                      <p className="text-xs text-muted-foreground mt-0.5">JPG, PNG, GIF, WEBP • Maks 10MB</p>
                    </div>
                    <input type="file" accept="image/*" className="hidden" disabled={uploading}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 10 * 1024 * 1024) { toast.error('Dosya 10MB\'dan büyük olamaz'); return; }
                        setUploading(true);
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await axios.post(`${API}/telegram-user/upload-image`, formData, {
                            headers: getAuthHeaders()
                          });
                          // Create preview
                          const reader = new FileReader();
                          reader.onload = (ev) => {
                            setUploadedImage({ filename: res.data.filename, size: res.data.size, preview: ev.target.result });
                          };
                          reader.readAsDataURL(file);
                          toast.success('Görsel yüklendi');
                        } catch (err) { toast.error(err.response?.data?.detail || 'Görsel yüklenemedi'); }
                        finally { setUploading(false); e.target.value = ''; }
                      }} />
                    {uploading && <RefreshCw className="w-5 h-5 animate-spin text-primary" />}
                  </label>
                )}
              </div>

              {/* Account */}
              {accounts.length > 1 ? (
                <div className="bg-card rounded-2xl border border-border/70 p-6 space-y-3">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center"><Phone className="w-5 h-5 text-primary" /></div>
                    <div><h2 className="text-base font-semibold text-foreground">Gönderen Hesap</h2><p className="text-xs text-muted-foreground">Hangi Telegram hesabından gönderilsin?</p></div>
                  </div>
                  {accounts.map(a => (
                    <button key={a.id} onClick={() => setSendingAccount(a)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition-colors ${sendingAccount?.id === a.id ? 'border-primary bg-primary/10' : 'border-border/70 hover:bg-secondary/50'}`}>
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1 text-left"><p className="text-sm font-medium text-foreground">{a.tg_first_name || ''} {a.tg_last_name || ''}</p><p className="text-xs text-muted-foreground">@{a.tg_username || a.tg_phone || '?'}</p></div>
                      {sendingAccount?.id === a.id && <Check className="w-5 h-5 text-primary" />}
                    </button>
                  ))}
                </div>
              ) : accounts.length === 1 && (
                <div className="bg-secondary/20 rounded-xl p-4 flex items-center gap-3">
                  <Phone className="w-4 h-4 text-primary" /><span className="text-sm text-muted-foreground">Gönderen:</span>
                  <span className="text-sm font-medium text-foreground">@{accounts[0]?.tg_username || accounts[0]?.tg_first_name || '?'}</span>
                </div>
              )}

              {/* Message Preview */}
              {(template.trim() || uploadedImage) && (
                <div className="bg-secondary/20 rounded-2xl p-5">
                  <p className="text-xs text-muted-foreground mb-3 font-medium">Mesaj Önizleme</p>
                  <div className="bg-[hsl(184,58%,18%)] rounded-2xl p-4 max-w-[80%]">
                    {uploadedImage?.preview && (
                      <img src={uploadedImage.preview} alt="Görsel" className="w-full max-h-40 object-cover rounded-xl mb-2" />
                    )}
                    {template.trim() && <p className="text-sm text-[hsl(198,50%,96%)] whitespace-pre-wrap leading-relaxed">{template}</p>}
                  </div>
                </div>
              )}

              <Button className="w-full rounded-xl h-14 text-base font-medium" onClick={() => setStep(mode === 'numbers' ? STEPS.PASTE : STEPS.RECIPIENTS)} disabled={!template.trim() || !sendingAccount}>
                <Send className="w-5 h-5 mr-2" /> Devam Et
              </Button>
            </div>
          )}

          {/* PASTE */}
          {step === STEPS.PASTE && (
            <div className="space-y-4">
              <div className="bg-card rounded-xl border border-border/70 p-5 space-y-3">
                <h2 className="text-sm font-semibold text-foreground flex items-center gap-2"><ClipboardPaste className="w-4 h-4 text-primary" /> Numaraları Yapıştırın</h2>
                <p className="text-xs text-muted-foreground">Her satıra bir numara veya virgülle ayırın. Ülke kodu ile birlikte girin.</p>
                <textarea value={pastedNumbers} onChange={e => setPastedNumbers(e.target.value)} rows={10}
                  className="w-full rounded-xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground font-mono placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                  placeholder={"+905551234567\n+905559876543\n+905557654321"} />
                <p className="text-[10px] text-muted-foreground">{numCount} numara bulundu (maks. {MAX_NUMBERS})</p>
              </div>
              <Button className="w-full rounded-xl h-12" onClick={checkNumbers} disabled={!pastedNumbers.trim() || checking || numCount > MAX_NUMBERS}>
                {checking ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Kontrol Ediliyor...</> : <><Search className="w-4 h-4 mr-2" /> Telegram'da Kontrol Et ({numCount})</>}
              </Button>
            </div>
          )}

          {/* CHECKING */}
          {step === STEPS.CHECKING && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <RefreshCw className="w-10 h-10 animate-spin text-primary" />
              <p className="text-sm text-foreground font-medium">Numaralar kontrol ediliyor...</p>
              <p className="text-xs text-muted-foreground">Telegram'a kayıtlı olanlar ayrılıyor</p>
            </div>
          )}

          {/* RECIPIENTS */}
          {step === STEPS.RECIPIENTS && (
            <div className="space-y-3">
              {mode === 'numbers' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-green-500/10 rounded-xl p-3 text-center"><UserCheck className="w-5 h-5 text-green-500 mx-auto mb-1" /><p className="text-lg font-bold text-green-500">{registered.length}</p><p className="text-[10px] text-muted-foreground">Telegram'da Kayıtlı</p></div>
                    <div className="bg-destructive/10 rounded-xl p-3 text-center"><UserX className="w-5 h-5 text-destructive mx-auto mb-1" /><p className="text-lg font-bold text-destructive">{notRegistered.length}</p><p className="text-[10px] text-muted-foreground">Kayıtsız</p></div>
                  </div>
                  {notRegistered.length > 0 && (
                    <div className="bg-card rounded-xl border border-destructive/20 p-3">
                      <p className="text-xs font-medium text-destructive mb-2">Kayıtsız Numaralar:</p>
                      <div className="max-h-24 overflow-y-auto"><p className="text-[10px] text-muted-foreground font-mono">{notRegistered.join(', ')}</p></div>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground">Kayıtlı Kişiler</h2>
                    <Badge variant="outline" className="text-xs">{selected.size} seçildi</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-xl text-xs h-7" onClick={() => setSelected(new Set(registered.map(r => r.user_id)))}>Tümünü Seç</Button>
                    <Button variant="outline" size="sm" className="rounded-xl text-xs h-7" onClick={() => setSelected(new Set())}>Seçimi Kaldır</Button>
                  </div>
                  <div className="bg-card rounded-xl border border-border/70 overflow-hidden">
                    <div className="max-h-[350px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {registered.map(r => (
                        <button key={r.user_id} onClick={() => toggleSelect(r.user_id)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-border/20 ${selected.has(r.user_id) ? 'bg-primary/10' : 'hover:bg-secondary/50'}`}>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selected.has(r.user_id) ? 'bg-primary border-primary' : 'border-border'}`}>{selected.has(r.user_id) && <Check className="w-3 h-3 text-primary-foreground" />}</div>
                          <Avatar className="w-8 h-8 flex-shrink-0"><AvatarFallback className={`${ac(r.name)} text-white text-[10px]`}>{ini(r.name)}</AvatarFallback></Avatar>
                          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-foreground truncate">{r.name}</p><p className="text-[10px] text-muted-foreground">{r.phone} {r.username ? `@${r.username}` : ''}</p></div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {mode === 'contacts' && (
                <>
                  <div className="flex items-center justify-between"><h2 className="text-sm font-semibold text-foreground">Alıcıları Seçin</h2><Badge variant="outline" className="text-xs">{selected.size} seçildi</Badge></div>
                  <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Kişi veya sohbet ara..." className="w-full pl-9 pr-3 py-2 rounded-xl bg-background border border-border/70 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40" /></div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-xl text-xs h-7" onClick={() => setSelected(new Set(filteredDialogs.map(d => d.id)))}>Tümünü Seç</Button>
                    <Button variant="outline" size="sm" className="rounded-xl text-xs h-7" onClick={() => setSelected(new Set())}>Seçimi Kaldır</Button>
                  </div>
                  <div className="bg-card rounded-xl border border-border/70 overflow-hidden">
                    <div className="max-h-[350px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {loadingDialogs ? <div className="p-6 text-center"><RefreshCw className="w-5 h-5 animate-spin text-primary mx-auto" /></div>
                       : filteredDialogs.length === 0 ? <p className="p-4 text-center text-sm text-muted-foreground">Sohbet bulunamadı</p>
                       : filteredDialogs.map(d => {
                        const Icon = d.chat_type === 'group' ? Users : d.chat_type === 'channel' ? Hash : User;
                        return (<button key={d.id} onClick={() => toggleSelect(d.id)} className={`w-full flex items-center gap-3 px-4 py-2.5 text-left border-b border-border/20 ${selected.has(d.id) ? 'bg-primary/10' : 'hover:bg-secondary/50'}`}>
                          <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${selected.has(d.id) ? 'bg-primary border-primary' : 'border-border'}`}>{selected.has(d.id) && <Check className="w-3 h-3 text-primary-foreground" />}</div>
                          <Avatar className="w-8 h-8 flex-shrink-0"><AvatarFallback className={`${ac(d.name)} text-white text-[10px]`}>{ini(d.name)}</AvatarFallback></Avatar>
                          <p className="text-sm font-medium text-foreground truncate flex-1 flex items-center gap-1"><Icon className="w-3 h-3 text-muted-foreground" /> {d.name}</p>
                        </button>);
                      })}
                    </div>
                  </div>
                </>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 rounded-xl" onClick={() => setStep(mode === 'numbers' ? STEPS.PASTE : STEPS.TEMPLATE)}>Geri</Button>
                <Button className="flex-1 rounded-xl" onClick={startSending} disabled={selected.size === 0}>
                  <Send className="w-4 h-4 mr-2" /> Otomatik Gönder ({selected.size})
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">Tüm seçilen kişilere otomatik olarak gönderilecek</p>
            </div>
          )}

          {/* SENDING (AUTO) */}
          {step === STEPS.SENDING && (
            <div className="space-y-4">
              <div className="bg-card rounded-xl border border-border/70 p-4 space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground"><span>İlerleme</span><span>{results.length} / {recipients.length}</span></div>
                <Progress value={prog} className="h-2.5" />
                <div className="flex gap-3 text-[10px]">
                  <span className="text-green-500">{stats.sent} gönderildi</span>
                  <span className="text-destructive">{stats.failed} başarısız</span>
                  <span className="text-muted-foreground">{recipients.length - results.length} kaldı</span>
                </div>
              </div>

              {/* Current */}
              {cur && (
                <div className="bg-card rounded-xl border border-primary/30 p-4 flex items-center gap-3">
                  {isSending && <RefreshCw className="w-5 h-5 animate-spin text-primary flex-shrink-0" />}
                  {!isSending && countdown > 0 && <div className="w-10 h-10 rounded-full border-2 border-primary flex items-center justify-center flex-shrink-0"><span className="text-sm font-bold text-primary">{countdown}</span></div>}
                  {!isSending && countdown === 0 && <Send className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
                  <Avatar className="w-9 h-9 flex-shrink-0"><AvatarFallback className={`${ac(cur.name)} text-white text-[10px]`}>{ini(cur.name)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{cur.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {cur.phone || ''} {isSending ? 'Gönderiliyor...' : isPaused ? 'Duraklatıldı' : countdown > 0 ? `Sonraki gönderim: ${countdown}sn` : 'Bekliyor'}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-[10px]">{currentIdx + 1}/{recipients.length}</Badge>
                </div>
              )}

              {/* Recent results */}
              {results.length > 0 && (
                <div className="bg-card rounded-xl border border-border/70 overflow-hidden">
                  <div className="max-h-[200px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {[...results].reverse().slice(0, 20).map((r, i) => (
                      <div key={`${r.id}-${i}`} className="flex items-center gap-2 px-3 py-1.5 border-b border-border/20">
                        {r.status === 'sent' ? <Check className="w-3 h-3 text-green-500 flex-shrink-0" /> : <XCircle className="w-3 h-3 text-destructive flex-shrink-0" />}
                        <p className="text-xs text-foreground truncate flex-1">{r.name}</p>
                        <span className="text-[9px] text-muted-foreground">{r.status === 'sent' ? 'Gönderildi' : 'Başarısız'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="flex gap-2">
                <Button variant={isPaused ? 'default' : 'outline'} className="flex-1 rounded-xl h-11" onClick={togglePause}>
                  {isPaused ? <><Play className="w-4 h-4 mr-2" /> Devam Et</> : <><Pause className="w-4 h-4 mr-2" /> Duraklat</>}
                </Button>
                <Button variant="destructive" className="flex-1 rounded-xl h-11" onClick={() => { if (window.confirm('İptal etmek istediğinize emin misiniz?')) cancelSending(); }}>
                  <X className="w-4 h-4 mr-2" /> İptal
                </Button>
              </div>

              <div className="bg-secondary/20 rounded-xl p-3 flex items-center gap-2">
                <Phone className="w-3.5 h-3.5 text-primary" /><span className="text-[10px] text-muted-foreground">Gönderen:</span>
                <span className="text-[10px] font-medium text-foreground">@{sendingAccount?.tg_username || sendingAccount?.tg_first_name || '?'}</span>
              </div>
            </div>
          )}

          {/* SUMMARY */}
          {step === STEPS.SUMMARY && (
            <div className="space-y-4">
              <div className="bg-card rounded-xl border border-border/70 p-5 text-center space-y-3">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
                <h2 className="text-lg font-semibold text-foreground">Gönderim Tamamlandı</h2>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-500/10 rounded-xl p-3"><p className="text-2xl font-bold text-green-500">{stats.sent}</p><p className="text-[10px] text-muted-foreground">Gönderildi</p></div>
                  <div className="bg-yellow-500/10 rounded-xl p-3"><p className="text-2xl font-bold text-yellow-500">{stats.skipped}</p><p className="text-[10px] text-muted-foreground">Atlandı</p></div>
                  <div className="bg-destructive/10 rounded-xl p-3"><p className="text-2xl font-bold text-destructive">{stats.failed}</p><p className="text-[10px] text-muted-foreground">Başarısız</p></div>
                </div>
              </div>
              <div className="bg-card rounded-xl border border-border/70 overflow-hidden">
                <div className="max-h-[300px] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
                  {results.map((r, i) => (
                    <div key={`${r.id}-${i}`} className="flex items-center gap-3 px-4 py-2 border-b border-border/20">
                      <Avatar className="w-7 h-7 flex-shrink-0"><AvatarFallback className={`${ac(r.name)} text-white text-[9px]`}>{ini(r.name)}</AvatarFallback></Avatar>
                      <p className="text-sm text-foreground flex-1 truncate">{r.name}</p>
                      {r.status === 'sent' && <Badge className="text-[9px] h-5 bg-green-500 text-white"><Check className="w-2.5 h-2.5 mr-0.5" /> Gönderildi</Badge>}
                      {r.status === 'skipped' && <Badge variant="secondary" className="text-[9px] h-5">Atlandı</Badge>}
                      {r.status === 'failed' && <Badge variant="destructive" className="text-[9px] h-5"><XCircle className="w-2.5 h-2.5 mr-0.5" /> Başarısız</Badge>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                {stats.failed > 0 && <Button variant="outline" className="w-full rounded-xl" onClick={() => retryGroup('failed')}><RefreshCw className="w-4 h-4 mr-2" /> Başarısızları Tekrar Dene ({stats.failed})</Button>}
                {stats.skipped > 0 && <Button variant="outline" className="w-full rounded-xl" onClick={() => retryGroup('skipped')}>Atlananlara Dön ({stats.skipped})</Button>}
                <Button className="w-full rounded-xl h-12" onClick={() => navigate('/app')}>Bitir</Button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
